import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";

function GuestChoicesEditor({
  guest,
  draftChoices,
  draftSkipSelections,
  onChange,
  onSkipSelectionsChange,
  onSave,
  skipItems,
  maxChoices,
}) {
  const [choices, setChoices] = useState([]);
  const [skipSelections, setSkipSelections] = useState([false, false, false]);
  const [addedCostsAck, setAddedCostsAck] = useState(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState("");

  useEffect(() => {
    const source = Array.isArray(draftChoices)
      ? draftChoices
      : Array.isArray(guest.choices)
        ? guest.choices.slice(0, maxChoices).map((c) => (typeof c === "string" ? c : c?.id || ""))
        : [];
    const initial = source.slice(0, maxChoices).map((c) => (typeof c === "string" ? c : c?.id || ""));
    while (initial.length < maxChoices) initial.push("");
    setChoices(initial);
    if (draftSkipSelections !== undefined) {
      const selections = Array.isArray(draftSkipSelections) ? draftSkipSelections : [];
      setSkipSelections([!!selections[0], !!selections[1], !!selections[2]]);
    } else {
      const selections = Array.isArray(guest.skipSelections) ? guest.skipSelections : [];
      setSkipSelections([!!selections[0], !!selections[1], !!selections[2]]);
    }
    setAddedCostsAck(!!guest.addedCosts);
  }, [guest.choices, guest.skipSelections, guest.addedCosts, draftChoices, draftSkipSelections, maxChoices]);

  const options = useMemo(() => guest.activities || [], [guest.activities]);
  const activityMap = useMemo(() => {
    const map = {};
    options.forEach((a) => {
      map[a.id] = a;
    });
    return map;
  }, [options]);

  const hasAddedCosts = useMemo(() => {
    return choices.some((choiceId) => {
      if (!choiceId) return false;
      const activity = activityMap[choiceId];
      if (!activity) return false;
      const costPerson = Number(activity.addedCostPerPerson ?? 0);
      const costActivity = Number(activity.addedCostPerActivity ?? 0);
      return costPerson > 0 || costActivity > 0;
    });
  }, [choices, activityMap]);

  function updateChoice(idx, value) {
    const next = [...choices];
    next[idx] = value || "";
    setChoices(next);
    onChange?.(guest.id, next);
  }

  function updateSkipSelection(idx, value) {
    const next = [...skipSelections];
    next[idx] = value;
    setSkipSelections(next);
    onSkipSelectionsChange?.(guest.id, next);
  }

  function isSelected(id, idx) {
    return choices.some((c, i) => i !== idx && c === id);
  }

  async function save() {
    setSaving(true);
    setStatus("");
    try {
      const ok = await onSave?.(guest.id, choices, skipSelections, hasAddedCosts ? addedCostsAck : undefined);
      setStatus(ok ? "Saved" : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 12 }}>
      <div style={{ fontWeight: 800 }}>{guest.name || "Guest"}</div>
      {(skipItems || [])
        .map((item, idx) => ({ item, idx }))
        .filter(({ item }) => item?.label)
        .map(({ item, idx }) => (
          <label
            key={`${item.label}-${idx}`}
            style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8 }}
          >
            <input
              type="checkbox"
              checked={!!skipSelections[idx]}
              onChange={(e) => updateSkipSelection(idx, e.target.checked)}
            />
            <span style={{ fontSize: 12, fontWeight: 700 }}>{item.label}</span>
          </label>
        ))}
      <div style={{ marginTop: 8, display: "grid", gap: 10 }}>
        {Array.from({ length: maxChoices }).map((_, idx) => {
          const selected = choices[idx] || "";
          const selectedActivity = activityMap[selected];
          return (
            <label key={idx} style={{ display: "grid", gap: 6 }}>
              <span style={{ fontWeight: 700 }}>Choice {idx + 1}</span>
            <select
              className="choice-select"
              value={selected}
              onChange={(e) => updateChoice(idx, e.target.value)}
            >
                <option value="">Select activity…</option>
                {options.map((a) => (
                  <option key={a.id} value={a.id} disabled={isSelected(a.id, idx)}>
                    {a.name || "(Unnamed activity)"}
                  </option>
                ))}
              </select>
              {selectedActivity?.url ? (
                <a href={selectedActivity.url} target="_blank" rel="noreferrer">
                  View activity
                </a>
              ) : null}
              {selectedActivity?.description ? (
                <div style={{ fontSize: 12, opacity: 0.7 }}>
                  {selectedActivity.description}
                </div>
              ) : null}
            </label>
          );
        })}
      </div>
      {hasAddedCosts && (
        <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12 }}>
          <input
            type="checkbox"
            checked={addedCostsAck}
            onChange={(e) => setAddedCostsAck(e.target.checked)}
          />
          <span style={{ fontSize: 12, fontWeight: 700 }}>
            I understand that some of the activities I am requesting have added costs
          </span>
        </label>
      )}
      <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center" }}>
        <button onClick={save} disabled={saving || (hasAddedCosts && !addedCostsAck)}>
          {saving ? "Saving…" : "Save Choices"}
        </button>
        {status && <span style={{ fontSize: 12, opacity: 0.7 }}>{status}</span>}
        {hasAddedCosts && !addedCostsAck && (
          <span style={{ fontSize: 12, opacity: 0.7 }}>
            Please confirm the added cost acknowledgement.
          </span>
        )}
      </div>
    </div>
  );
}

export default function FamilyChoicesPage() {
  const { familyId } = useParams();
  const [data, setData] = useState(null);
  const [maxChoices, setMaxChoices] = useState(5);
  const [error, setError] = useState("");
  const [guestDetails, setGuestDetails] = useState([]);
  const [draftChoices, setDraftChoices] = useState({});
  const [draftSkipSelections, setDraftSkipSelections] = useState({});
  const [savingAll, setSavingAll] = useState(false);
  const [saveAllStatus, setSaveAllStatus] = useState("");
  const [applyFromGuestId, setApplyFromGuestId] = useState("");
  const [applyStatus, setApplyStatus] = useState("");
  const [saveAllAddedCostsAck, setSaveAllAddedCostsAck] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError("");
      const res = await fetch(`/api/families/${familyId}/choices`);
      const json = await res.json();
      if (!res.ok) {
        if (!cancelled) setError(json?.error || "Failed to load family choices");
        return;
      }
      if (!cancelled) {
        setData(json);
        const nextMax = Math.max(1, Math.min(10, Number(json.maxChoices || 5)));
        setMaxChoices(nextMax);
      }
    }
    if (familyId) load();
    return () => {
      cancelled = true;
    };
  }, [familyId]);

  useEffect(() => {
    let cancelled = false;
    async function loadGuestChoices() {
      if (!data?.guests?.length) {
        setGuestDetails([]);
        return;
      }
      const res = await fetch(`/api/families/${familyId}/choices?includeActivities=1`);
      const json = await res.json();
      if (!res.ok) return;
      const results = json.guests || [];
      if (!cancelled) {
        setGuestDetails(results);
        const initialDrafts = {};
        const initialNoSatPm = {};
        results.forEach((g) => {
          const list = Array.isArray(g.choices)
            ? g.choices.slice(0, maxChoices).map((c) => (typeof c === "string" ? c : c?.id || ""))
            : [];
          while (list.length < maxChoices) list.push("");
          initialDrafts[g.id] = list;
          const selections = Array.isArray(g.skipSelections) ? g.skipSelections : [];
          initialNoSatPm[g.id] = [!!selections[0], !!selections[1], !!selections[2]];
        });
        setDraftChoices(initialDrafts);
        setDraftSkipSelections(initialNoSatPm);
      }
    }
    loadGuestChoices();
    return () => {
      cancelled = true;
    };
  }, [data, familyId, maxChoices]);

  function handleChoiceChange(guestId, nextChoices) {
    setDraftChoices((prev) => ({ ...prev, [guestId]: nextChoices }));
  }

  function applySelectionsToAll() {
    if (!applyFromGuestId) return;
    const sourceChoices = draftChoices[applyFromGuestId];
    if (!sourceChoices) return;
    const sourceSkipSelections = draftSkipSelections[applyFromGuestId] || [false, false, false];
    const normalized = Array.isArray(sourceChoices) ? sourceChoices.slice(0, maxChoices) : [];
    while (normalized.length < maxChoices) normalized.push("");
    setDraftChoices((prev) => {
      const next = { ...prev };
      guestDetails.forEach((g) => {
        next[g.id] = normalized;
      });
      return next;
    });
    setDraftSkipSelections((prev) => {
      const next = { ...prev };
      guestDetails.forEach((g) => {
        next[g.id] = [...sourceSkipSelections];
      });
      return next;
    });
    setApplyStatus("Applied to everyone. Please review and save.");
  }

  function handleSkipSelectionsChange(guestId, value) {
    setDraftSkipSelections((prev) => ({ ...prev, [guestId]: value }));
  }

  const activityMap = useMemo(() => {
    const map = {};
    (guestDetails || []).forEach((guest) => {
      (guest.activities || []).forEach((activity) => {
        map[activity.id] = activity;
      });
    });
    return map;
  }, [guestDetails]);

  const hasAddedCostsAny = useMemo(() => {
    return (guestDetails || []).some((guest) => {
      const choices = draftChoices[guest.id] || guest.choices || [];
      return choices.some((choiceId) => {
        if (!choiceId) return false;
        const id = typeof choiceId === "string" ? choiceId : choiceId?.id;
        if (!id) return false;
        const activity = activityMap[id];
        if (!activity) return false;
        const costPerson = Number(activity.addedCostPerPerson ?? 0);
        const costActivity = Number(activity.addedCostPerActivity ?? 0);
        return costPerson > 0 || costActivity > 0;
      });
    });
  }, [guestDetails, draftChoices, activityMap]);

  async function saveGuestChoices(guestId, choices, skipSelections, addedCosts) {
    const res = await fetch(`/api/guests/${guestId}/choices`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        choices: Array.isArray(choices) ? choices.slice(0, maxChoices) : [],
        skipSelections,
        ...(addedCosts !== undefined ? { addedCosts } : {}),
      }),
    });
    const json = await res.json();
    if (res.ok) setApplyStatus("");
    return res.ok ? true : (json?.error || "Failed to save");
  }

  async function saveAll() {
    setSavingAll(true);
    setSaveAllStatus("");
    try {
      const entries = Object.entries(draftChoices);
      const results = await Promise.all(
        entries.map(async ([guestId, choices]) => ({
          guestId,
          ok: await saveGuestChoices(
            guestId,
            choices,
            draftSkipSelections[guestId],
            hasAddedCostsAny ? saveAllAddedCostsAck : undefined
          ),
        }))
      );
      const failed = results.filter((r) => r.ok !== true);
      if (failed.length) {
        const failedNames = failed
          .map((r) => guestDetails.find((g) => g.id === r.guestId)?.name || r.guestId)
          .join(", ");
        setSaveAllStatus(`Saved with errors: ${failedNames}`);
      } else {
        setSaveAllStatus("All choices saved.");
      }
      setApplyStatus("");
      if (!hasAddedCostsAny) setSaveAllAddedCostsAck(false);
    } finally {
      setSavingAll(false);
    }
  }

  if (error) return <div style={{ padding: 16, color: "crimson" }}>{error}</div>;
  if (!data) return <div style={{ padding: 16 }}>Loading choices…</div>;

  return (
    <div style={{ maxWidth: 760, margin: "0 auto", padding: 16 }}>
      <style>{`
        @media (max-width: 640px) {
          .family-choices-grid {
            grid-template-columns: 1fr !important;
          }
          .choice-select {
            font-size: 16px;
          }
        }
      `}</style>
      <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 16 }}>
      <div style={{ fontSize: 20, fontWeight: 800 }}>{data.name || "Family"}</div>
      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>Activity choices</div>
      <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
        Max choices per guest: {maxChoices}
      </div>
      {data.infoText ? (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            fontWeight: 700,
            opacity: 0.9,
            whiteSpace: "pre-wrap",
          }}
        >
          {data.infoText}
        </div>
      ) : null}
      <div style={{ marginTop: 8 }}>
        <a
          href="https://www.brushcreekranch.com/activities#activities-grid"
            target="_blank"
            rel="noreferrer"
          >
            View all activities
          </a>
        </div>
        {guestDetails.length > 1 && (
          <div style={{ marginTop: 12, display: "grid", gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 700 }}>Apply selections to everyone</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <select
                value={applyFromGuestId}
                onChange={(e) => setApplyFromGuestId(e.target.value)}
              >
                <option value="">Select guest…</option>
                {guestDetails.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name || "Guest"}
                  </option>
                ))}
              </select>
              <button onClick={applySelectionsToAll} disabled={!applyFromGuestId}>
                Apply to all
              </button>
            </div>
            {applyStatus && <div style={{ fontSize: 12, opacity: 0.7 }}>{applyStatus}</div>}
          </div>
        )}
      </div>

      <div style={{ marginTop: 16, display: "grid", gap: 10 }}>
        {hasAddedCostsAny && (
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input
              type="checkbox"
              checked={saveAllAddedCostsAck}
              onChange={(e) => setSaveAllAddedCostsAck(e.target.checked)}
            />
            <span style={{ fontSize: 12, fontWeight: 700 }}>
              I understand that some of the activities I am requesting have added costs
            </span>
          </label>
        )}
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            onClick={saveAll}
            disabled={savingAll || guestDetails.length === 0 || (hasAddedCostsAny && !saveAllAddedCostsAck)}
          >
          {savingAll ? "Saving…" : "Save all"}
          </button>
          {saveAllStatus && <span style={{ fontSize: 12, opacity: 0.7 }}>{saveAllStatus}</span>}
          {hasAddedCostsAny && !saveAllAddedCostsAck && (
            <span style={{ fontSize: 12, opacity: 0.7 }}>
              Please confirm the added cost acknowledgement.
            </span>
          )}
        </div>
      </div>

      <div className="family-choices-grid" style={{ marginTop: 12, display: "grid", gap: 12 }}>
        {guestDetails.map((guest) => (
          <GuestChoicesEditor
            key={guest.id}
            guest={guest}
            draftChoices={draftChoices[guest.id]}
            draftSkipSelections={draftSkipSelections[guest.id]}
            onChange={handleChoiceChange}
            onSkipSelectionsChange={handleSkipSelectionsChange}
            onSave={saveGuestChoices}
            skipItems={data?.skipItems}
            maxChoices={maxChoices}
          />
        ))}
        {guestDetails.length === 0 && (
          <div style={{ opacity: 0.7 }}>No guests found.</div>
        )}
      </div>
    </div>
  );
}
